import Constants from '../../utils/constants';
import { helper } from '../../utils/helper';

/**
 * Couleur du marqueur sur la carte en fonction du `status` (entier) renvoyé
 * par l'API.
 *
 * Codes :
 *   0 = DONE          (Lavé)
 *   1 = TODO          (Pas encore lavé)
 *   2 = PROBLEME      (Problème remonté)
 *   3 = INACCESSIBLE  (Lavage impossible)
 *
 * On réutilise `helper.getColorForStatus` pour rester aligné avec les autres
 * écrans (PatrimoineDetailList, badges, etc.). On en extrait juste le
 * `backgroundColor` car Leaflet n'attend qu'une couleur de remplissage.
 *
 * Fallback (TODO / valeur inconnue) : un gris neutre lisible sur la carte.
 */
export const getStatusColor = (status) => {
    const sx = helper.getColorForStatus(status);
    // Pour TODO, le helper renvoie "#eee" — trop clair sur fond carte.
    // On force un gris plus contrasté pour la cartographie uniquement.
    if (status == Constants.CONTENEURS_STATUS_TODO || status == null) {
        return '#9E9E9E';
    }
    return sx?.backgroundColor || '#9E9E9E';
};

/**
 * Libellé d'un type de conteneur à partir du code (CA, CE, AB, ...).
 * Utilise les données en dur de Constants.CONTENEURS_TYPE.
 */
export const getConteneurTypeLabel = (code) => {
    if (!code) return '';
    const found = (Constants.CONTENEURS_TYPE || []).find((t) => t.code === code);
    return found ? found.libelle : code;
};

export const getDechetTypeLabel = (code) => {
    if (!code) return '';
    const found = (Constants.DECHET_TYPES || []).find((t) => t.code === code);
    return code;
};

/**
 * Statuts de lavage proposés dans le filtre du mode « Lavage » (codes int de l'API).
 * Un `status` null est traité comme TODO (« Non lavé »), comme pour la couleur carte.
 */
export const STATUT_FILTER_OPTIONS = [
    { code: Constants.CONTENEURS_STATUS_TODO, label: Constants.CONTENEURS_STATUS_TODO_LIBELLE },
    { code: Constants.CONTENEURS_STATUS_DONE, label: Constants.CONTENEURS_STATUS_DONE_LIBELLE },
    { code: Constants.CONTENEURS_STATUS_PROBLEME, label: Constants.CONTENEURS_STATUS_PROBLEME_LIBELLE },
    { code: Constants.CONTENEURS_STATUS_INACCESSIBLE, label: Constants.CONTENEURS_STATUS_INACCESSIBLE_LIBELLE },
];

/** Code de statut de lavage normalisé (null / inconnu → TODO). */
export const normalizeStatut = (status) => {
    const n = Number(status);
    if (status === null || status === undefined || status === '' || Number.isNaN(n)) return Constants.CONTENEURS_STATUS_TODO;
    return STATUT_FILTER_OPTIONS.some((o) => o.code === n) ? n : Constants.CONTENEURS_STATUS_TODO;
};

/** Compteurs par statut de lavage (chips du filtre), sur les rows passés. */
export const countStatuts = (rows) => {
    const counts = {};
    STATUT_FILTER_OPTIONS.forEach((o) => { counts[o.code] = 0; });
    (rows || []).forEach((r) => { counts[normalizeStatut(r.status)] += 1; });
    return counts;
};

/**
 * Convertit une FeatureCollection GeoJSON en `rows` plats consommables par
 * la DataGrid, en injectant lat/lng pour éviter de recroiser avec features.
 */
export const featuresToRows = (features) => {
    if (!Array.isArray(features)) return [];
    return features.map((f) => {
        const props = f?.properties || {};
        const coords = f?.geometry?.coordinates || [];
        const lng = props.longitude ?? coords[0];
        const lat = props.latitude ?? coords[1];
        return {
            ...props,
            lat: typeof lat === 'number' ? lat : parseFloat(lat),
            lng: typeof lng === 'number' ? lng : parseFloat(lng),
        };
    });
};

// ---------------------------------------------------------------------------
// Mode « Maintenance » / état checklist (PAT-04 regle-gestion R1-R5, R8, R9)
// ---------------------------------------------------------------------------

export const CHECKLIST_ETAT = {
    RAS: 'RAS',
    PROBLEME: 'PROBLEME',
    TENTATIVE_IMPOSSIBLE: 'TENTATIVE_IMPOSSIBLE',
    NON_INSPECTE: 'NON_INSPECTE',
};

/** Ordre d'affichage des filtres / légende. */
export const CHECKLIST_ETAT_OPTIONS = [
    { code: CHECKLIST_ETAT.RAS, label: 'RAS' },
    { code: CHECKLIST_ETAT.PROBLEME, label: 'Problème' },
    { code: CHECKLIST_ETAT.TENTATIVE_IMPOSSIBLE, label: 'Tentative impossible' },
    { code: CHECKLIST_ETAT.NON_INSPECTE, label: 'Non inspecté' },
];

export const getChecklistEtatLabel = (etat) =>
    CHECKLIST_ETAT_OPTIONS.find((o) => o.code === etat)?.label ?? '';

/**
 * Couleur d'un conteneur en mode Maintenance (R4/R5, DESIGN.md §1) :
 *   RAS                  → vert maison `--thm-base`
 *   PROBLEME             → orange `--thm-orange`, 3 paliers par nb d'éléments en défaut (1 / 2 / ≥ 3)
 *   TENTATIVE_IMPOSSIBLE → rouge (même rouge que « lavage impossible »)
 *   NON_INSPECTE / absent → gris nodata (même gris que TODO en mode Lavage)
 */
export const CHECKLIST_COLORS = {
    RAS: '#88bc24',
    PROBLEME_1: '#F7B76A',
    PROBLEME_2: '#F39A3F',
    PROBLEME_3: '#D8721C',
    TENTATIVE_IMPOSSIBLE: '#B22222',
    NON_INSPECTE: '#9E9E9E',
};

export const getChecklistColor = (checklist) => {
    const etat = checklist?.etat;
    if (etat === CHECKLIST_ETAT.RAS) return CHECKLIST_COLORS.RAS;
    if (etat === CHECKLIST_ETAT.TENTATIVE_IMPOSSIBLE) return CHECKLIST_COLORS.TENTATIVE_IMPOSSIBLE;
    if (etat === CHECKLIST_ETAT.PROBLEME) {
        const n = Number(checklist?.nbProblemes) || 1;
        if (n >= 3) return CHECKLIST_COLORS.PROBLEME_3;
        if (n === 2) return CHECKLIST_COLORS.PROBLEME_2;
        return CHECKLIST_COLORS.PROBLEME_1;
    }
    return CHECKLIST_COLORS.NON_INSPECTE;
};

/** Poids heatmap (R8) : nb d'éléments en défaut ; tout autre état = 0 (absent de la heatmap). */
export const getChecklistWeight = (checklist) =>
    (checklist?.etat === CHECKLIST_ETAT.PROBLEME ? (Number(checklist?.nbProblemes) || 0) : 0);

/** Codes des éléments en défaut d'un état checklist. */
export const getChecklistElementsEnDefaut = (checklist) =>
    (checklist?.elements ?? []).filter((e) => e?.statut === 'PROBLEME');

/**
 * Filtre R9 : `etats` = codes d'état retenus (vide → tous), `elements` = codes d'éléments
 * (vide → pas de contrainte). Un élément sélectionné restreint les PROBLEME à ceux ayant au moins
 * un de ces éléments en défaut ; les autres états sélectionnés passent tels quels.
 */
export const filterRowsByChecklist = (rows, etats, elements) => {
    const etatSet = Array.isArray(etats) && etats.length > 0 ? new Set(etats) : null;
    const elSet = Array.isArray(elements) && elements.length > 0 ? new Set(elements) : null;
    if (!etatSet && !elSet) return rows;
    return rows.filter((r) => {
        const c = r.checklist;
        const etat = c?.etat ?? CHECKLIST_ETAT.NON_INSPECTE;
        if (etatSet && !etatSet.has(etat)) return false;
        if (elSet) {
            // Seul un filtre d'éléments (sans état) restreint aux PROBLEME ; sinon l'état déjà admis passe.
            if (etat !== CHECKLIST_ETAT.PROBLEME) return etatSet != null;
            return getChecklistElementsEnDefaut(c).some((e) => elSet.has(e.code ?? e.libelle));
        }
        return true;
    });
};

/** Compteurs par état (légende / chips), sur les rows passés. */
export const countChecklistEtats = (rows) => {
    const counts = { RAS: 0, PROBLEME: 0, TENTATIVE_IMPOSSIBLE: 0, NON_INSPECTE: 0 };
    (rows || []).forEach((r) => {
        const etat = r.checklist?.etat ?? CHECKLIST_ETAT.NON_INSPECTE;
        if (counts[etat] !== undefined) counts[etat] += 1;
    });
    return counts;
};

/** Plage par défaut R6 : 90 jours glissants jusqu'à aujourd'hui, au format YYYY-MM-DD. */
export const defaultChecklistRange = () => {
    const toIso = (d) => {
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${m}-${day}`;
    };
    const fin = new Date();
    const debut = new Date(fin);
    debut.setDate(debut.getDate() - 90);
    return { dateDebut: toIso(debut), dateFin: toIso(fin) };
};
